import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Auth now lives in an httpOnly cookie set by the backend (not readable from
  // JS), so on mount we simply ask the backend who -if anyone- is logged in.
  const fetchCurrentUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    setUser(response.data.user);
    return response.data.user;
  };

  const register = async (email, password, name, team) => {
    const response = await axios.post(`${API_URL}/auth/register`, {
      email,
      password,
      name,
      team
    });
    setUser(response.data.user);
    return response.data.user;
  };

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`);
    } catch (error) {
      console.error('Logout request failed:', error);
    }
    setUser(null);
  }, []);

  // Kept for backward compatibility with existing call sites that spread this
  // into axios `headers`; the httpOnly cookie now carries authentication.
  const getAuthHeader = useCallback(() => ({}), []);

  const refreshUser = useCallback(async () => {
    await fetchCurrentUser();
  }, [fetchCurrentUser]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        getAuthHeader,
        refreshUser,
        isAuthenticated: !!user
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
